import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';

import { useAuth } from '@/lib/useAuth';
import { apiRequest } from '@/lib/api';
import { computeRailState } from '@/lib/onboardingState';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/CopyButton';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import { cn } from '@/lib/utils';

const POLL_INTERVAL_MS = 1_500;

// Direct URL to the Google Ads page where the customer accepts Customer Data
// Terms. Best-guess as of April 2026 — verify and update during sub-task 16
// if Google's URL has shifted.
const DPT_ACCEPTANCE_URL = 'https://ads.google.com/aw/conversions/customerdataterms';

interface SetupStatus {
  customer_id: string | null;
  lead_action_done: boolean;
  qualified_action_done: boolean;
  purchase_action_done: boolean;
  ec_enabled: boolean;
  dpt_required: boolean;
  done: boolean;
}

function ProgressRow({
  label,
  done,
  active,
}: {
  label: string;
  done: boolean;
  active: boolean;
}) {
  return (
    <li className="flex items-center gap-3 py-1.5 text-sm">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        {done ? (
          <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        ) : active ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <span className="h-2 w-2 rounded-full bg-muted-foreground/30" />
        )}
      </span>
      <span className={cn(done ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
    </li>
  );
}

export function OnboardingGoogleAdsSetupPage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [retrying, setRetrying] = useState(false);

  // Direct-URL arrivals with no customer_id yet → back to picker (or further
  // back to connect if they don't even have a refresh token).
  // System admins can preview the page for QA — they have no
  // google_ads_customer_id by design and would otherwise loop.
  useEffect(() => {
    if (me?.is_system_admin) return;
    if (me && !me.google_ads_customer_id) {
      navigate('/onboarding/connect/google-pick', { replace: true });
    }
  }, [me, navigate]);

  const statusQuery = useQuery({
    queryKey: ['oauth', 'google-ads', 'setup-status'],
    queryFn: () => apiRequest<SetupStatus>('/oauth/google-ads/setup-status'),
    refetchInterval: (q) =>
      // Stop polling once we're done OR blocked on DPT (user has to act).
      q.state.data?.done || q.state.data?.dpt_required ? false : POLL_INTERVAL_MS,
    enabled: !!me?.google_ads_customer_id,
  });

  const status = statusQuery.data;

  // When `done` flips, refresh `/me` so the dashboard gate routes correctly.
  const completedRef = useRef(false);
  useEffect(() => {
    if (status?.done && !completedRef.current) {
      completedRef.current = true;
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    }
  }, [status?.done, queryClient]);

  async function handleRetry() {
    if (!me?.google_ads_customer_id) return;
    setRetrying(true);
    try {
      // Re-trigger provisioning by POSTing the same customer_id again.
      // The orchestrator is idempotent — created actions are skipped, EC
      // re-attempted (now that DPT is accepted).
      await apiRequest('/oauth/google-ads/select-account', {
        method: 'POST',
        body: { customer_id: me.google_ads_customer_id },
      });
      // Force a fresh poll right away.
      await queryClient.invalidateQueries({
        queryKey: ['oauth', 'google-ads', 'setup-status'],
      });
    } finally {
      setRetrying(false);
    }
  }

  // Derive the "currently active" step for the spinner — the first not-yet-done step.
  const leadActive = !status?.lead_action_done;
  const qualifiedActive = status?.lead_action_done && !status?.qualified_action_done;
  const purchaseActive =
    status?.qualified_action_done && !status?.purchase_action_done;
  const ecActive =
    status?.purchase_action_done &&
    !status?.ec_enabled &&
    !status?.dpt_required;

  return (
    <OnboardingShell
      current={3}
      title="Setting up your Google Ads"
      subtitle={
        `Creating conversion actions and enabling Enhanced Conversions on account ${me?.google_ads_customer_id ?? '…'}.`
      }
      state={computeRailState(me ?? null)}
    >
     <div className="space-y-5">
      <ul className="rounded-2xl border bg-card shadow-card p-4">
        <ProgressRow
          label="Conversion action: HeyMotek - Lead"
          done={!!status?.lead_action_done}
          active={leadActive}
        />
        <ProgressRow
          label="Conversion action: HeyMotek - Qualified"
          done={!!status?.qualified_action_done}
          active={!!qualifiedActive}
        />
        <ProgressRow
          label="Conversion action: HeyMotek - Purchase"
          done={!!status?.purchase_action_done}
          active={!!purchaseActive}
        />
        <ProgressRow
          label="Enable Enhanced Conversions for Leads"
          done={!!status?.ec_enabled}
          active={!!ecActive}
        />
      </ul>

      {/* DPT-blocked state — user has to accept terms in Google Ads. */}
      {status?.dpt_required && (
        <Banner variant="warn" className="p-4">
          <div className="space-y-4">
            <div>
              <p className="font-semibold">Customer Data Terms required.</p>
              <p className="mt-1 text-muted-foreground">
                Google needs you to accept these terms before we can share your contacts with your
                Google Ads account. That's what helps Google find more people like the ones who
                already become your customers.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-medium">Direct link</label>
              <div className="flex items-center gap-2">
                <a
                  href={DPT_ACCEPTANCE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 truncate rounded border bg-muted/30 px-2 py-1.5 font-mono text-xs underline underline-offset-2 hover:no-underline"
                >
                  {DPT_ACCEPTANCE_URL}
                </a>
                <CopyButton value={DPT_ACCEPTANCE_URL} size="sm" />
              </div>
            </div>

            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>Open the link above (already drops you on the right page).</li>
              <li>Review and accept the Customer Data Terms.</li>
              <li>Come back and click <strong>I've accepted — retry</strong> below.</li>
            </ol>

            <Button
              type="button"
              variant="outline"
              onClick={handleRetry}
              disabled={retrying}
              className="w-full"
            >
              {retrying ? 'retrying...' : "I've accepted — retry"}
            </Button>
          </div>
        </Banner>
      )}

      {/* Done — Continue navigates to '/' and lets RequireClient route
          based on next_step (dashboard if onboarding complete, otherwise
          the next outstanding step). */}
      {status?.done && (
        <Button
          type="button"
          className="w-full rounded-full bg-brand text-white hover:bg-brand-dark py-3.5 text-base font-bold"
          onClick={() => navigate('/')}
        >
          Continue
        </Button>
      )}

      {/* Still working — disabled Continue while in progress. */}
      {!status?.done && !status?.dpt_required && (
        <Button
          type="button"
          className="w-full rounded-full bg-brand/40 text-white py-3.5 text-base font-bold cursor-not-allowed"
          disabled
        >
          {ecActive ? 'enabling Enhanced Conversions…' : 'setting up…'}
        </Button>
      )}
     </div>
    </OnboardingShell>
  );
}
